import React, { useEffect, useState, useMemo } from 'react';

import { useParams, Link } from 'react-router-dom';

import Navbar from '../components/Navbar';
import ResponsiveImage from '../components/ResponsiveImage';
import { Helmet } from 'react-helmet-async';

import publicApi from '../services/publicApi';

import { normalizeStorageUrl } from '../config/apiBase';

import { useTranslation } from 'react-i18next';
import { usePublicPaths } from '../routes/publicPaths';
import { programSlugFromCategory } from '../config/donateBanks';

import './ProjetDetail.css';

const FACEBOOK_PLAYER_WIDTH = 768;
const FACEBOOK_PLAYER_HEIGHT = 432;

const isFacebookVideoEmbed = (src) =>
  typeof src === 'string' && src.includes('facebook.com/plugins/video.php');

const sizedFacebookVideoSrc = (src) => {
  if (!isFacebookVideoEmbed(src)) return src;
  try {
    const url = new URL(src);
    url.searchParams.set('width', String(FACEBOOK_PLAYER_WIDTH));
    url.searchParams.set('height', String(FACEBOOK_PLAYER_HEIGHT));
    url.searchParams.set('show_text', 'false');
    return url.toString();
  } catch {
    return src;
  }
};

const ProjetDetail = () => {

  const { t } = useTranslation();
  const paths = usePublicPaths();
  const { slug } = useParams();

  const [projet, setProjet] = useState(null);

  const [loading, setLoading] = useState(true);

  const [activeMedia, setActiveMedia] = useState(0);



  useEffect(() => {

    setLoading(true);

    publicApi.get(`/public/projets/${slug}`)

      .then((res) => {

        setProjet(res.data);

        setActiveMedia(0);

      })

      .catch(() => setProjet(null))

      .finally(() => setLoading(false));

  }, [slug]);



  const mediaItems = useMemo(() => {

    if (!projet) return [];



    const items = [];

    const pushImage = (url, webp, webpSm) => {
      const normalized = normalizeStorageUrl(url);
      if (normalized && !items.some((item) => item.type === 'image' && item.src === normalized)) {
        items.push({
          type: 'image',
          src: normalized,
          webp: normalizeStorageUrl(webp) || null,
          webpSm: normalizeStorageUrl(webpSm) || null,
        });
      }
    };

    pushImage(projet.image_url, projet.image_url_webp, projet.image_url_webp_sm);

    (projet.gallery_urls || []).forEach((url) => pushImage(url));



    if (projet.video_embed_src) {

      items.push({ type: 'embed', src: projet.video_embed_src });

    }

    if (projet.video_file_url) {

      items.push({ type: 'file', src: normalizeStorageUrl(projet.video_file_url) });

    }



    return items;

  }, [projet]);



  const mapEmbedSrc = useMemo(() => projet?.map_embed_src || null, [projet]);



  const mapsLink = useMemo(() => {

    if (projet?.latitude != null && projet?.longitude != null) {

      return `https://www.google.com/maps?q=${projet.latitude},${projet.longitude}`;

    }

    if (mapEmbedSrc) {

      return mapEmbedSrc.replace('/maps/embed', '/maps').replace('output=embed', '');

    }

    return null;

  }, [projet, mapEmbedSrc]);



  const activeItem = mediaItems[activeMedia];



  if (loading) {

    return <div className="min-h-screen flex items-center justify-center">{t('public.projet.loading')}</div>;

  }



  if (!projet) {

    return (

      <div className="min-h-screen flex flex-col items-center justify-center" dir="rtl">

        <h2 className="text-2xl font-bold mb-4">{t('public.projet.notFound')}</h2>

        <Link to={paths.home} className="text-brand-primary hover:underline">{t('public.projet.backHome')}</Link>

      </div>

    );

  }

  const donateProgram = programSlugFromCategory(projet.category);

  return (

    <div className="min-h-screen bg-gray-50">

      <Helmet>
        <title>{`${projet.title} | ${t('public.brand.titleSuffix')}`}</title>
        <meta name="description" content={(projet.description || '').slice(0, 160)} />
      </Helmet>

      <Navbar />

      <div className="projet-detail-page">

        <div className="projet-detail-card">

          {mediaItems.length > 0 && (

            <div className="projet-detail-gallery">

              <div className={`projet-detail-gallery__main-wrap${activeItem?.type !== 'image' ? ' projet-detail-gallery__main-wrap--video' : ''}`}>

                {activeItem?.type === 'image' && (

                  <ResponsiveImage
                    src={activeItem.src}
                    webp={activeItem.webp}
                    webpSm={activeItem.webpSm}
                    alt={projet.title}
                    className="projet-detail-gallery__main"
                    loading="eager"
                    decoding="async"
                  />

                )}

                {activeItem?.type === 'embed' && (

                  <div className={`projet-detail-gallery__video-frame${isFacebookVideoEmbed(activeItem.src) ? ' projet-detail-gallery__video-frame--facebook' : ''}`}>

                    <iframe

                      title={t('public.program.videoTitle', { title: projet.title })}

                      src={sizedFacebookVideoSrc(activeItem.src)}

                      className="projet-detail-gallery__video-embed"

                      scrolling="no"

                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"

                      allowFullScreen

                    />

                  </div>

                )}

                {activeItem?.type === 'file' && (

                  <div className="projet-detail-gallery__video-frame">

                    <video

                      className="projet-detail-gallery__video-file"

                      src={activeItem.src}

                      controls

                      playsInline

                    />

                  </div>

                )}

              </div>

              {mediaItems.length > 1 && (

                <div className="projet-detail-gallery__thumbs">

                  {mediaItems.map((item, index) => (

                    <button

                      key={`${item.type}-${item.src}`}

                      type="button"

                      className={`projet-detail-gallery__thumb${index === activeMedia ? ' is-active' : ''}${item.type !== 'image' ? ' is-video' : ''}`}

                      onClick={() => setActiveMedia(index)}

                      aria-label={item.type === 'image' ? t('public.projet.image', { n: index + 1 }) : t('public.projet.video')}

                    >

                      {item.type === 'image' ? (

                        <img src={item.src} alt="" loading="lazy" decoding="async" />

                      ) : (

                        <span className="projet-detail-gallery__video-icon" aria-hidden>▶</span>

                      )}

                    </button>

                  ))}

                </div>

              )}

            </div>

          )}



          <div className="projet-detail-body">

            {(projet.category_label || projet.category) && (

              <span className="projet-detail-category">{projet.category_label || projet.category}</span>

            )}

            <h1 className="projet-detail-title">{projet.title}</h1>

            {projet.region && <p className="projet-detail-region">{projet.region}</p>}

            {projet.description && (

              <p className="projet-detail-description">{projet.description}</p>

            )}



            <div className="projet-detail-progress">

              <div className="projet-detail-progress__labels">

                <span>{projet.progress_percent}%</span>

                <span>

                  {parseFloat(projet.collected_amount).toLocaleString('fr-FR')} /{' '}

                  {parseFloat(projet.goal_amount).toLocaleString('fr-FR')} {t('public.common.currency')}

                </span>

              </div>

              <div className="projet-detail-progress__bar">

                <div style={{ width: `${projet.progress_percent}%` }} />

              </div>

            </div>



            {mapEmbedSrc && (

              <section className="projet-detail-map">

                <h2 className="projet-detail-section-title">{t('public.projet.mapTitle')}</h2>

                <div className="projet-detail-map__frame-wrap">

                  <iframe

                    title={t('public.projet.mapIframe', { title: projet.title })}

                    src={mapEmbedSrc}

                    loading="lazy"

                    referrerPolicy="no-referrer-when-downgrade"

                    allowFullScreen

                  />

                </div>

                {mapsLink && (

                  <a

                    href={mapsLink}

                    target="_blank"

                    rel="noopener noreferrer"

                    className="projet-detail-map__link"

                  >

                    {t('public.projet.openMaps')}

                  </a>

                )}

              </section>

            )}



            <Link

              to={`${paths.donate}?projet=${projet.slug}${donateProgram ? `&program=${donateProgram}` : ''}`}

              className="projet-detail-donate"

            >

              {t('public.projet.donate')}

            </Link>

          </div>

        </div>

      </div>

    </div>

  );

};



export default ProjetDetail;


